"use client";

type CaptureContext = Record<string, unknown>;

const dsn = process.env.NEXT_PUBLIC_GLITCHTIP_DSN;
const environment = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || "development";
const release = process.env.NEXT_PUBLIC_APP_RELEASE;

function eventId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : Math.random().toString(16).slice(2).padEnd(32, "0").slice(0, 32);
}

function envelopeUrl(value: string): string | null {
  try {
    const parsed = new URL(value);
    const projectId = parsed.pathname.replace(/^\/+/, "").split("/").pop();
    if (!projectId || !parsed.username) return null;
    const params = new URLSearchParams({
      sentry_key: parsed.username,
      sentry_version: "7",
      sentry_client: "artemahr-browser/1.0",
    });
    return `${parsed.protocol}//${parsed.host}/api/${projectId}/envelope/?${params}`;
  } catch {
    return null;
  }
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      type: error.name || "Error",
      value: error.message || "Browser error",
      stacktrace: error.stack ? { frames: [{ filename: "browser", function: error.stack }] } : undefined,
    };
  }
  return {
    type: "Error",
    value: typeof error === "string" ? error : JSON.stringify(error),
  };
}

export function captureBrowserException(error: unknown, context: CaptureContext = {}): void {
  if (!dsn || typeof window === "undefined") return;

  const url = envelopeUrl(dsn);
  if (!url) return;

  const id = eventId();
  const payload = {
    event_id: id,
    timestamp: new Date().toISOString(),
    platform: "javascript",
    level: "error",
    environment,
    release,
    tags: {
      project: "cfmoto",
      service: "frontend",
      location: window.location.pathname,
    },
    extra: context,
    exception: { values: [normalizeError(error)] },
    request: {
      url: window.location.href,
      headers: {
        "User-Agent": navigator.userAgent,
      },
    },
  };

  const envelope = [
    JSON.stringify({ event_id: id, dsn, sent_at: new Date().toISOString() }),
    JSON.stringify({ type: "event" }),
    JSON.stringify(payload),
  ].join("\n");

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([envelope], { type: "text/plain;charset=UTF-8" }));
    return;
  }

  void fetch(url, {
    method: "POST",
    body: envelope,
    keepalive: true,
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
  }).catch(() => {});
}

export function initGlitchTipBrowserCapture(): void {
  if (!dsn || typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    captureBrowserException(event.error || event.message, {
      source: "window.error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureBrowserException(event.reason, { source: "unhandledrejection" });
  });
}
